import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Check, ArrowRight } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import ServiceTestimonials from '../../components/ServiceTestimonials';
import ContactFormDialog from '../../components/ContactFormDialog';
import { Button } from '../../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';

const AmazonBrandRegistry = () => {
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const pricingPlans = [
    { name: "Registry Basic", price: "$199", features: ["Trademark Filing for Amazon", "Brand Registry Application", "Basic Seller Setup", "Email Support"] },
    { name: "Registry Pro", price: "$399", popular: true, features: ["Everything in Basic", "Comprehensive Trademark Search", "A+ Content Setup Guide", "Brand Protection Monitoring", "Priority Support", "Sponsored Brands Setup"] },
    { name: "Registry Elite", price: "$699", features: ["Everything in Pro", "Dedicated Account Manager", "Full IP Protection Strategy", "Listing Optimization Review", "Ongoing Compliance Support", "Multi-brand Registration"] }
  ];

  const process = [
    { step: "01", title: "Trademark Filing", description: "We file your trademark with the USPTO, ensuring it meets Amazon Brand Registry requirements for enrollment." },
    { step: "02", title: "Registry Application", description: "Once your trademark is filed, we submit your Brand Registry application and guide you through Amazon's verification process." },
    { step: "03", title: "Enrollment & Tools", description: "Get enrolled and access A+ Content, Sponsored Brands, IP protection tools, and enhanced brand analytics." }
  ];

  const testimonials = [
    { name: "Jennifer Rodriguez", role: "Amazon Seller", text: "Brand Registry enrollment opened up A+ Content and brand protection tools. My product listings improved dramatically." },
    { name: "Tom Richards", role: "Private Label Seller", text: "The team handled my trademark and Brand Registry enrollment seamlessly. My sales increased significantly." },
    { name: "Sarah Kim", role: "Amazon FBA Seller", text: "Professional service from trademark filing to Brand Registry approval. Everything was coordinated perfectly." },
    { name: "Chris Johnson", role: "Online Retailer", text: "Finally got access to Sponsored Brands and brand analytics. The IP protection alone was worth the investment." },
    { name: "Maria Lopez", role: "Amazon Seller", text: "Enrolled in Brand Registry within weeks. The guided process made everything simple and stress-free." }
  ];

  const faqs = [
    { question: "What is Amazon Brand Registry?", answer: "Amazon Brand Registry is a program that gives brand owners access to powerful tools for protecting and managing their brand on Amazon, including A+ Content, Sponsored Brands, and IP violation reporting." },
    { question: "Do I need a registered trademark for Brand Registry?", answer: "You need either an active registered trademark or a pending trademark application filed through the USPTO. We handle the trademark filing as part of our Brand Registry service." },
    { question: "How long does Brand Registry enrollment take?", answer: "Once your trademark is filed with the USPTO, Brand Registry enrollment typically takes 2-4 weeks. The full process depends on USPTO trademark processing times." },
    { question: "What tools do I get with Brand Registry?", answer: "Brand Registry unlocks A+ Content for enhanced product listings, Sponsored Brands advertising, Brand Analytics, IP violation reporting tools, and automated brand protection features." }
  ];

  return (
    <div className="min-h-screen bg-wine-950">
      <Header onOpenContactForm={() => setIsContactFormOpen(true)} />
      
      <section className="pt-24 pb-16 bg-gradient-to-b from-wine-900/50 to-wine-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Link to="/" className="inline-flex items-center text-gold-400 hover:text-gold-500 mb-6 text-sm font-medium transition-colors"><ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Home</Link>
          <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight mb-4">Amazon Brand Registry</h1>
          <p className="text-lg text-wine-300 max-w-2xl mx-auto">Enroll in Amazon Brand Registry with expert trademark filing and brand protection support.</p>
        </div>
      </section>

      <section className="py-20 bg-cream-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="rounded-2xl overflow-hidden h-80 shadow-lg">
              <img src="https://images.unsplash.com/photo-1646193186175-4ec30e6e5882?w=800&h=600&fit=crop" alt="E-commerce product listing on laptop screen" className="w-full h-full object-cover" />
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-wine-500 mb-3">About This Service</p>
              <h2 className="text-3xl font-bold text-wine-950 mb-4">Unlock the Full Power of Amazon Selling</h2>
              <p className="text-stone-600 leading-relaxed mb-4">Amazon Brand Registry gives sellers access to A+ Content, Sponsored Brands advertising, brand analytics, and powerful IP protection tools. A registered trademark is required to enroll.</p>
              <p className="text-stone-600 leading-relaxed mb-6">Our team handles everything: trademark filing with the USPTO, Brand Registry application, and enrollment verification. We ensure your brand meets all of Amazon's requirements for seamless approval.</p>
              <Button onClick={() => setIsContactFormOpen(true)} className="bg-wine-600 hover:bg-wine-700 text-white transition-colors">Enroll in Brand Registry</Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-wine-900/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white text-center mb-12">Our 3-Step Process</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {process.map((item) => (
              <div key={item.step} className="bg-wine-950 p-8 rounded-xl border border-wine-800/40 hover:border-gold-500/30 hover:-translate-y-1 hover:shadow-xl transition-all duration-300">
                <div className="w-10 h-10 bg-wine-600 rounded-lg flex items-center justify-center text-white font-bold text-sm mb-4">{item.step}</div>
                <h3 className="text-xl font-semibold text-white mb-3">{item.title}</h3>
                <p className="text-sm text-wine-400 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-cream-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-wine-950 text-center mb-12">Brand Registry Packages</h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {pricingPlans.map((plan) => (
              <div key={plan.name} className={`relative bg-white p-8 rounded-xl border-2 ${plan.popular ? 'border-wine-600 shadow-xl shadow-wine-600/10' : 'border-stone-200'} hover:-translate-y-1 hover:shadow-xl hover:border-gold-400 transition-all duration-300`}>
                {plan.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2"><span className="bg-wine-600 text-white px-3 py-1 rounded-full text-xs font-bold">MOST POPULAR</span></div>}
                <h3 className="text-xl font-bold text-wine-950 mb-2">{plan.name}</h3>
                <div className="text-4xl font-bold text-wine-950 mb-6">{plan.price}</div>
                <ul className="space-y-3 mb-8">{plan.features.map((f, i) => (<li key={i} className="flex items-start"><Check className="w-4 h-4 text-wine-600 mr-2 mt-0.5 flex-shrink-0" /><span className="text-sm text-stone-600">{f}</span></li>))}</ul>
                <Button onClick={() => setIsContactFormOpen(true)} className={`w-full ${plan.popular ? 'bg-wine-600 hover:bg-wine-700 text-white' : 'bg-stone-100 hover:bg-gold-500 hover:text-wine-950 text-stone-800'} transition-colors`}>Choose {plan.name}</Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-wine-900 text-center">
        <div className="max-w-3xl mx-auto px-4">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold-400 mb-3">Get Started</p>
          <h2 className="text-3xl font-bold text-white mb-4">Start Selling with Brand Protection</h2>
          <p className="text-wine-200 mb-8">Get your trademark filed and enroll in Amazon Brand Registry to unlock the tools that grow your business.</p>
          <Button onClick={() => setIsContactFormOpen(true)} className="bg-gold-500 hover:bg-gold-400 text-wine-950 font-semibold px-8 py-5 text-base transition-colors">Enroll Now <ArrowRight className="ml-2 w-4 h-4" /></Button>
        </div>
      </section>

      <ServiceTestimonials testimonials={testimonials} serviceName="Amazon Brand Registry" />

      <section className="py-20" style={{ background: 'linear-gradient(180deg, #1A0A0E 0%, #0D0507 100%)' }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold-400 mb-3 text-center">FAQ</p>
          <h2 className="text-3xl font-bold text-white text-center mb-10">Frequently Asked Questions</h2>
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="bg-wine-900/50 rounded-xl px-6 border border-wine-800/40 hover:border-gold-400/30 transition-colors data-[state=open]:border-gold-400/40">
                <AccordionTrigger className="text-left text-base font-medium text-wine-100 hover:text-gold-400 py-5 [&[data-state=open]]:text-gold-400">{faq.question}</AccordionTrigger>
                <AccordionContent className="text-wine-300 text-sm leading-relaxed pb-5">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <Footer onOpenContactForm={() => setIsContactFormOpen(true)} />
      <ContactFormDialog isOpen={isContactFormOpen} onClose={() => setIsContactFormOpen(false)} />
    </div>
  );
};

export default AmazonBrandRegistry;
